import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Save, Trash2, Loader2, AlertTriangle, Plus, Info,
  ChevronDown, ChevronRight, Check,
} from 'lucide-react';
import { BRAND } from '../../brand';
import { claimRules as claimRulesUrl } from '../BaseURL';
import { confirmClaim, notifyClaim } from './claimsDialog';

// ============================================================================
//  Claim Rules — the format a claim's identifiers must satisfy
// ============================================================================
//
//  NHIA rejects a claim whose membership number or claim check code is the wrong
//  shape, and what the right shape IS differs by scheme. Until this screen there
//  was nowhere to say so: a character count sat on the insurance company and was
//  read by the patient registration form and by nothing else, so a wrong-length
//  identifier on a claim was invisible until a batch came back.
//
//  Setting a rule here does two things at once:
//
//    · the Advanced Filter gains a count — "Membership number is the wrong
//      format · 65 claims" — so a month can be cleaned before it is exported
//    · the claim editor states it on the claim itself
//
//  Both read the SAME rule through claimFieldRules.js, in SQL for the filter and
//  in JavaScript for the editor, which is why they cannot disagree.
//
//  ── ONE SCHEME AT A TIME ───────────────────────────────────────────────────
//
//  The first version of this screen grouped by FIELD: a "Membership number"
//  section listing every scheme, then a "CCC" section listing every scheme
//  again. Every scheme therefore appeared twice, the page ran to six near-
//  identical cards for two real settings, and the duplication read as a bug.
//
//  It is now grouped by SCHEME. You pick the insurer you are configuring and set
//  both of its identifiers together, which is how the job is actually done —
//  nobody sets the membership-number rule for four schemes and then comes back
//  round for the CCCs. Each card also folds, so a scheme that is already correct
//  collapses to one line.
//
//  ── THE THING THAT STILL CONFUSES EVERYONE ────────────────────────────────
//
//  The two claim models do not agree on what a scheme is called:
//
//      imported claims (XML)   'NHIS'
//      claims raised here      'National Health Insurance Authority'
//
//  They are the same insurer and they need TWO rules, because a rule is matched
//  on the string the claim actually carries and nothing can know those two are
//  the same. Picking a scheme rather than listing them all makes this much less
//  jarring, but the "Why twice?" explainer stays.

const CARD = {
  background: BRAND.surface,
  border: `1px solid ${BRAND.line}`,
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(15,31,61,.06)',
};

const LABEL = {
  fontSize: 10.5, fontWeight: 800, letterSpacing: .5,
  textTransform: 'uppercase', color: BRAND.inkFaint,
  display: 'block', marginBottom: 5,
};

const input = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  fontSize: 13, fontFamily: 'inherit', color: BRAND.ink,
  background: BRAND.surface, outline: 'none',
  border: `1px solid ${BRAND.line}`, borderRadius: 8,
};

const btn = (tone) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
  font: 'inherit', fontSize: 12.5, fontWeight: 700,
  border: `1px solid ${tone === 'ghost' ? BRAND.line : 'transparent'}`,
  background: tone === 'ghost' ? BRAND.surface
    : tone === 'danger' ? BRAND.dangerSoft : BRAND.green,
  color: tone === 'ghost' ? BRAND.inkMuted
    : tone === 'danger' ? BRAND.danger : '#fff',
});

//  One rule, as the form holds it. Lengths are kept as STRINGS so an empty box
//  stays empty — coercing to 0 here would read as "length zero", which is a rule
//  nothing can satisfy rather than the absence of one.
const toForm = (r) => ({
  field: r.field,
  scheme_name: r.scheme_name || '',
  enabled: r.enabled !== false,
  exact_length: r.exact_length == null ? '' : String(r.exact_length),
  min_length: r.min_length == null ? '' : String(r.min_length),
  max_length: r.max_length == null ? '' : String(r.max_length),
  charset: r.charset || 'any',
  severity: r.severity || 'error',
  note: r.note || '',
});

const DEFAULT_KEY = '';   // '' = the rule used by every scheme without its own

export default function ClaimRules() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({
    fields: [], charsets: [], severities: [], schemes: [], active: true,
  });
  const [rules, setRules] = useState([]);

  //  Which insurer is being configured. '' is the default rule; null means the
  //  first load has not chosen one yet.
  const [scheme, setScheme] = useState(null);

  //  Folded state, per field, for the scheme on screen. Folding is per FIELD
  //  rather than per (field, scheme) because only one scheme is ever visible —
  //  keying it on both would remember a fold for a card nobody can see.
  const [folded, setFolded] = useState({});

  //  Edited copies, keyed field::scheme. A rule is only in here once it has been
  //  touched, which is what makes Save per-card rather than a page-wide button
  //  that might write rules you never looked at.
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);

  const keyOf = (field, s) => `${field}::${s || ''}`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(claimRulesUrl);
      if (data?.success) {
        setRules(data.results.rules || []);
        setMeta({
          fields: data.results.fields || [],
          charsets: data.results.charsets || [],
          severities: data.results.severities || [],
          schemes: data.results.schemes || [],
          active: data.results.active !== false,
        });
        setDrafts({});
      } else {
        setError(data?.message || 'Could not load the rules');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  //  Every scheme worth offering: the default, then every scheme that has a rule,
  //  then every scheme the claims carry that does not have one yet. Sorted, with
  //  the default pinned first.
  const schemeTabs = useMemo(() => {
    const withRules = new Set(rules.map((r) => r.scheme_name).filter(Boolean));
    const inUse = new Set(meta.schemes || []);
    const all = [...new Set([...withRules, ...inUse])]
      .sort((a, b) => a.localeCompare(b));
    return [DEFAULT_KEY, ...all];
  }, [rules, meta.schemes]);

  //  Land on the default rule rather than on nothing. Only ever runs once, when
  //  the tabs first arrive.
  useEffect(() => {
    if (scheme === null && schemeTabs.length) setScheme(DEFAULT_KEY);
  }, [scheme, schemeTabs]);

  const ruleFor = (field) => rules.find(
    (r) => r.field === field && (r.scheme_name || '') === (scheme || '')
  ) || null;

  const draftFor = (r) => drafts[keyOf(r.field, r.scheme_name)] || toForm(r);
  const dirty = (r) => !!drafts[keyOf(r.field, r.scheme_name)];

  const patch = (r, k, v) => setDrafts((d) => ({
    ...d,
    [keyOf(r.field, r.scheme_name)]: { ...draftFor(r), [k]: v },
  }));

  const fieldLabel = (key) => meta.fields.find((f) => f.key === key)?.label || key;
  const charsetLabel = (key) =>
    meta.charsets.find((c) => c.key === key)?.label || key;

  // ── Writes ────────────────────────────────────────────────────────────────

  const save = async (r) => {
    const form = draftFor(r);
    const k = keyOf(r.field, r.scheme_name);

    if (form.exact_length && (form.min_length || form.max_length)) {
      const ok = await confirmClaim({
        title: 'Keep the exact length?',
        lead: 'An exact length already pins both ends, so the minimum and maximum '
            + 'will be cleared.',
        confirmText: 'Save with exact length',
      });
      if (!ok) return;
    }

    setBusy(k);
    setError(null);
    try {
      const { data } = await axios.put(claimRulesUrl, {
        field: form.field,
        scheme_name: form.scheme_name || null,
        enabled: form.enabled,
        exact_length: form.exact_length,
        min_length: form.min_length,
        max_length: form.max_length,
        charset: form.charset,
        severity: form.severity,
        note: form.note,
      });
      if (data?.success) {
        setRules(data.results || []);
        setDrafts((d) => { const n = { ...d }; delete n[k]; return n; });
      } else {
        setError(data?.message || 'Could not save');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(null);
    }
  };

  const removeOverride = async (r) => {
    const ok = await confirmClaim({
      title: 'Remove this rule?',
      lead: `${r.scheme_name} will fall back to the default rule for `
          + `${fieldLabel(r.field)}.`,
      confirmText: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;

    setBusy(keyOf(r.field, r.scheme_name));
    try {
      const { data } = await axios.delete(claimRulesUrl, {
        params: { field: r.field, scheme_name: r.scheme_name },
      });
      if (data?.success) setRules(data.results || []);
      else setError(data?.message || 'Could not remove the rule');
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(null);
    }
  };

  //  Give this scheme its own rule for a field it currently inherits. Seeded from
  //  the DEFAULT rule rather than blank, so an override starts from whatever is
  //  already in force for it.
  const addRule = async (field) => {
    const k = keyOf(field, scheme);
    setBusy(k);
    try {
      const base = rules.find((r) => r.field === field && !r.scheme_name);
      const { data } = await axios.put(claimRulesUrl, {
        field,
        scheme_name: scheme || null,
        enabled: true,
        exact_length: base?.exact_length ?? '',
        min_length: base?.min_length ?? '',
        max_length: base?.max_length ?? '',
        charset: base?.charset || 'digits',
        severity: base?.severity || 'error',
        note: 'Added from Claim Rules.',
      });
      if (data?.success) {
        setRules(data.results || []);
        setFolded((f) => ({ ...f, [field]: false }));
      } else {
        setError(data?.message || 'Could not add the rule');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(null);
    }
  };

  const explain = () => notifyClaim({
    title: 'Why one insurer can need two rules',
    lead: 'A rule is matched on the scheme name the claim itself carries, and the '
        + 'two claim sources do not use the same one:',
    items: [
      'Claims imported from an NHIA XML file carry the short name, such as "NHIS".',
      'Claims raised here at the OPD carry the full insurance company name.',
      'Nothing can know those two are the same insurer, so each needs its own rule.',
    ],
    note: 'The default rule covers every scheme that has no rule of its own, so you '
        + 'can set one default and only override the schemes that differ.',
  });

  // ── One line describing a rule, for the folded header ─────────────────────
  const summarise = (f) => {
    if (!f.enabled) return 'Not enforced';
    const parts = [];
    if (f.exact_length) parts.push(`exactly ${f.exact_length} characters`);
    else if (f.min_length || f.max_length) {
      parts.push(`${f.min_length || 1}–${f.max_length || '∞'} characters`);
    }
    if (f.charset && f.charset !== 'any') parts.push(charsetLabel(f.charset).toLowerCase());
    return parts.length ? parts.join(', ') : 'No length set — characters only';
  };

  const tabLabel = (s) => (s === DEFAULT_KEY ? 'Every scheme (default)' : s);

  //  How many of this scheme's fields have a rule, for the tab badge — so the
  //  picker says where work is still needed without opening each one.
  const coverage = (s) => meta.fields.filter(
    (f) => rules.some((r) => r.field === f.key && (r.scheme_name || '') === s)
  ).length;

  return (
    <div style={{ minHeight: '100%', background: BRAND.canvas, padding: '14px 16px 28px' }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ ...CARD, padding: '16px 18px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: BRAND.greenSoft, display: 'grid', placeItems: 'center',
          }}>
            <ShieldCheck size={20} style={{ color: BRAND.greenDark }} />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: BRAND.ink }}>
              Claim Rules
            </div>
            <div style={{ fontSize: 12.5, color: BRAND.inkMuted, marginTop: 3, lineHeight: 1.6 }}>
              Pick an insurance scheme, then set the format its identifiers must have
              before NHIA will accept a claim.
            </div>
          </div>
          <button type="button" onClick={explain} style={btn('ghost')}>
            <Info size={14} /> Why twice?
          </button>
        </div>
      </div>

      {/* ── Not installed ──────────────────────────────────────────────── */}
      {!loading && !meta.active && (
        <div style={{
          ...CARD, padding: '14px 16px', marginBottom: 14,
          background: '#FFF8EC', borderColor: 'rgba(224,139,47,.35)',
          display: 'flex', gap: 10, alignItems: 'flex-start',
        }}>
          <AlertTriangle size={17} style={{ color: BRAND.warning, flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12.5, color: '#7A4E12', lineHeight: 1.6 }}>
            <b>These rules are not live yet.</b> The <code>claim_field_rules</code> table
            is missing, so nothing reads what you set here and the two format checks do
            not appear in the Advanced Filter. Apply
            {' '}<b>migration 049_claim_field_rules.sql</b> and restart the backend.
          </div>
        </div>
      )}

      {error && (
        <div style={{
          ...CARD, padding: '12px 14px', marginBottom: 14,
          background: BRAND.dangerSoft, borderColor: 'rgba(214,69,69,.3)',
          fontSize: 12.5, color: '#A62828',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ ...CARD, padding: 40, textAlign: 'center', color: BRAND.inkMuted }}>
          <Loader2 size={20} className="spin" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 13 }}>Loading the rules…</div>
        </div>
      ) : (
        <>
          {/* ── Which scheme ─────────────────────────────────────────────
               A picker rather than every scheme listed at once. This is the
               change that removed the duplication: one insurer on screen, both
               of its identifiers together. */}
          <div style={{ ...CARD, padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ ...LABEL, marginBottom: 8 }}>Insurance scheme</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {schemeTabs.map((s) => {
                const on = scheme === s;
                const have = coverage(s);
                const total = meta.fields.length;
                return (
                  <button key={s || '(default)'} type="button"
                    onClick={() => { setScheme(s); setFolded({}); }}
                    aria-pressed={on}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 7,
                      padding: '8px 12px', borderRadius: 9, cursor: 'pointer',
                      font: 'inherit', fontSize: 12.5,
                      fontWeight: on ? 800 : 600,
                      border: `1px solid ${on ? BRAND.greenDark : BRAND.line}`,
                      background: on ? BRAND.greenSoft : BRAND.surface,
                      color: on ? BRAND.greenDark : BRAND.inkSoft,
                    }}>
                    {tabLabel(s)}
                    {/*  How many of this scheme's fields it actually governs. A
                         scheme showing 0 of 2 inherits everything from the
                         default, which is a legitimate state and not an error. */}
                    <span style={{
                      fontSize: 10.5, fontWeight: 800, padding: '2px 6px',
                      borderRadius: 20, whiteSpace: 'nowrap',
                      background: have === total ? BRAND.greenDark
                        : have ? '#fff' : '#EEF2F7',
                      color: have === total ? '#fff'
                        : have ? BRAND.navy : BRAND.inkFaint,
                      border: `1px solid ${have && have !== total ? BRAND.line : 'transparent'}`,
                    }}>
                      {have} of {total}
                    </span>
                  </button>
                );
              })}
            </div>

            {scheme === DEFAULT_KEY && (
              <div style={{ fontSize: 11.5, color: BRAND.inkMuted, marginTop: 9, lineHeight: 1.6 }}>
                This rule applies to every scheme that has no rule of its own. Set it once
                here, then override only the schemes that differ.
              </div>
            )}
          </div>

          {/* ── The chosen scheme's fields ──────────────────────────────── */}
          {meta.fields.map((fieldDef) => {
            const r = ruleFor(fieldDef.key);
            const isFolded = folded[fieldDef.key] === true;

            //  No rule of its own: this field falls through to the default, said
            //  plainly with the option to override.
            if (!r) {
              const base = rules.find((x) => x.field === fieldDef.key && !x.scheme_name);
              return (
                <div key={fieldDef.key} style={{ ...CARD, marginBottom: 12, padding: '14px 16px' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: BRAND.ink }}>
                        {fieldDef.label}
                      </div>
                      <div style={{ fontSize: 12, color: BRAND.inkMuted, marginTop: 3 }}>
                        Uses the default rule
                        {base ? ` — ${summarise(toForm(base))}` : ''}
                      </div>
                    </div>
                    <button type="button" onClick={() => addRule(fieldDef.key)}
                      disabled={!!busy} style={btn('ghost')}>
                      <Plus size={14} /> Set a rule for this scheme
                    </button>
                  </div>
                </div>
              );
            }

            const f = draftFor(r);
            const k = keyOf(r.field, r.scheme_name);
            const isDefault = !r.scheme_name;
            const saving = busy === k;
            const noLength = !f.exact_length && !f.min_length && !f.max_length;

            return (
              <div key={fieldDef.key} style={{ ...CARD, marginBottom: 12, overflow: 'hidden' }}>
                {/* ── The fold header ──────────────────────────────────────
                     The whole strip is the fold control, so there is no small
                     chevron to hit. It carries the summary too, which is what
                     makes a folded card still worth reading. */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', flexWrap: 'wrap',
                  borderBottom: isFolded ? 'none' : `1px solid ${BRAND.line}`,
                  background: dirty(r) ? BRAND.greenSoft : BRAND.canvas,
                }}>
                  <button type="button"
                    onClick={() => setFolded((s) => ({ ...s, [fieldDef.key]: !isFolded }))}
                    aria-expanded={!isFolded}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9,
                      flex: 1, minWidth: 180, padding: 0,
                      border: 0, background: 'none', cursor: 'pointer',
                      font: 'inherit', textAlign: 'left',
                    }}>
                    {isFolded
                      ? <ChevronRight size={17} style={{ color: BRAND.inkFaint, flexShrink: 0 }} />
                      : <ChevronDown size={17} style={{ color: BRAND.inkFaint, flexShrink: 0 }} />}
                    <span style={{ minWidth: 0 }}>
                      <span style={{
                        display: 'block', fontSize: 14, fontWeight: 800, color: BRAND.ink,
                      }}>
                        {fieldDef.label}
                      </span>
                      <span style={{
                        display: 'block', fontSize: 11.5, marginTop: 2,
                        color: f.enabled ? BRAND.inkMuted : BRAND.inkFaint,
                      }}>
                        {summarise(f)}
                        {dirty(r) ? ' · unsaved' : ''}
                      </span>
                    </span>
                  </button>

                  {/*  Enforce, Remove and Save stay on the header so a folded
                       card can still be switched off or saved — folding is for
                       reading, not a reason to have to unfold to act. */}
                  <label style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 12.5, color: BRAND.inkSoft, cursor: 'pointer',
                  }}>
                    <input type="checkbox" checked={f.enabled}
                      onChange={(e) => patch(r, 'enabled', e.target.checked)} />
                    Enforce
                  </label>

                  {!isDefault && (
                    <button type="button" onClick={() => removeOverride(r)}
                      disabled={saving} style={{ ...btn('danger'), padding: '6px 10px' }}>
                      <Trash2 size={13} /> Remove
                    </button>
                  )}
                  <button type="button" onClick={() => save(r)}
                    disabled={saving || !dirty(r)}
                    style={{
                      ...btn(), padding: '6px 12px',
                      opacity: (saving || !dirty(r)) ? .55 : 1,
                      cursor: (saving || !dirty(r)) ? 'not-allowed' : 'pointer',
                    }}>
                    {saving ? <Loader2 size={13} className="spin" />
                      : dirty(r) ? <Save size={13} /> : <Check size={13} />}
                    {saving ? 'Saving…' : dirty(r) ? 'Save' : 'Saved'}
                  </button>
                </div>

                {!isFolded && (
                  <div style={{ padding: 14 }}>
                    <div style={{
                      display: 'grid', gap: 10,
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    }}>
                      <div>
                        <label style={LABEL}>Exact length</label>
                        <input type="number" min="1" value={f.exact_length}
                          placeholder="not set"
                          onChange={(e) => patch(r, 'exact_length', e.target.value)}
                          style={input} />
                      </div>
                      <div>
                        <label style={LABEL}>Minimum length</label>
                        <input type="number" min="1" value={f.min_length}
                          disabled={!!f.exact_length}
                          placeholder={f.exact_length ? 'not used' : 'not set'}
                          onChange={(e) => patch(r, 'min_length', e.target.value)}
                          style={{ ...input, opacity: f.exact_length ? .5 : 1 }} />
                      </div>
                      <div>
                        <label style={LABEL}>Maximum length</label>
                        <input type="number" min="1" value={f.max_length}
                          disabled={!!f.exact_length}
                          placeholder={f.exact_length ? 'not used' : 'not set'}
                          onChange={(e) => patch(r, 'max_length', e.target.value)}
                          style={{ ...input, opacity: f.exact_length ? .5 : 1 }} />
                      </div>
                      <div>
                        <label style={LABEL}>Allowed characters</label>
                        <select value={f.charset}
                          onChange={(e) => patch(r, 'charset', e.target.value)}
                          style={input}>
                          {meta.charsets.map((c) => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={LABEL}>Severity</label>
                        <select value={f.severity}
                          onChange={(e) => patch(r, 'severity', e.target.value)}
                          style={input}>
                          {meta.severities.map((s) => (
                            <option key={s} value={s}>
                              {s === 'error' ? 'Error — NHIA rejects it' : 'Warning — worth a look'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div style={{ marginTop: 10 }}>
                      <label style={LABEL}>Note</label>
                      <input type="text" value={f.note} maxLength={255}
                        placeholder="Why this rule is set the way it is"
                        onChange={(e) => patch(r, 'note', e.target.value)}
                        style={input} />
                    </div>

                    {/*  What this rule currently does, in words. A rule with no
                         length checks only the character class, and that is easy
                         to set without realising. */}
                    <div style={{
                      marginTop: 10, fontSize: 11.5, lineHeight: 1.6,
                      color: noLength && f.enabled ? '#7A4E12' : BRAND.inkMuted,
                      background: noLength && f.enabled ? '#FFF8EC' : 'transparent',
                      border: `1px solid ${noLength && f.enabled
                        ? 'rgba(224,139,47,.28)' : 'transparent'}`,
                      borderRadius: 7,
                      padding: noLength && f.enabled ? '7px 9px' : 0,
                    }}>
                      {!f.enabled
                        ? 'Turned off — this rule flags nothing.'
                        : noLength
                          ? 'No length is set, so only the character rule is checked. '
                            + 'Enter the number of characters this scheme issues to catch '
                            + 'wrong-length identifiers.'
                          : f.exact_length
                            ? `Flags any value that is not exactly ${f.exact_length} `
                              + `characters, or that contains characters outside `
                              + `"${charsetLabel(f.charset)}".`
                            : `Flags any value outside `
                              + `${f.min_length || 1}–${f.max_length || '∞'} characters.`}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }
               .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
}
